/**
 * Payer Connections staff roster (GDN-3408; PRD Slice B, AC-13 to AC-18).
 *
 * Every organization with one or more enrollment rows and one or more
 * non-Live payers, worst-blocked first, so staff can see at a glance who is
 * waiting on JotPsych, on the customer, or on the payer — and jump straight
 * into that org's clinic view.
 *
 * Reads `GET /v2/admin/enrollments/live` on mount (poll-Stedi-on-load, per
 * the Slice B ruling) and persists nothing: no writes, no polling interval,
 * no Statsig events (admin tooling is exempt — src/web-app/CLAUDE.md).
 *
 * The endpoint ships in smartscribe-server PR #4410 (open, merge-ready) —
 * until that merges, this view's query 404s on staging.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Hub } from '@mui/icons-material';
import { Alert, Box, Button, Tooltip, Typography } from '@mui/material';

import { BlockedOnChip, NO_TIMESTAMP_TOOLTIP, RosterChipStrip } from '@features/payer-connections';

import { BackButton } from '../components/back-button';
import { FilterableDataTable } from '../components/filterable-data-table';
import { usePayerConnectionsRosterQuery } from '../store/hooks';
import { getStalenessColor } from '../utils/formatting';

import {
  buildPayerConnectionsRoster,
  formatCheckedAtLine,
  type RosterTableRow,
} from './payer-connections-rows';

function formatDaysBlocked(days: number | null): string {
  return days == null ? '—' : `${days}d`;
}

/** Milliseconds from `from` to the next local midnight. */
function msUntilNextLocalMidnight(from: Date): number {
  const nextMidnight = new Date(
    from.getFullYear(),
    from.getMonth(),
    from.getDate() + 1,
    0,
    0,
    0,
    0,
  );
  return nextMidnight.getTime() - from.getTime();
}

/**
 * A "now" that only actually needs to change once a day: it re-renders the
 * checked-at line's same-day/different-day branch at the next local
 * midnight, then reschedules for the one after that. Reading `new Date()`
 * inline at render (the earlier fix) only helps if something re-renders —
 * a tab left open with no other state change never would. No repo-wide
 * `useNow`/`useCurrentTime` hook exists to reuse (checked before adding
 * this), so it's local to this view rather than invented as a shared one.
 */
function useMidnightTick(): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const scheduleNext = () => {
      timer = setTimeout(() => {
        setNow(new Date());
        scheduleNext();
      }, msUntilNextLocalMidnight(new Date()));
    };
    scheduleNext();
    return () => clearTimeout(timer);
  }, []);

  return now;
}

export default function PayerConnections() {
  const { data, error, isLoading, refetch } = usePayerConnectionsRosterQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const midnightTick = useMidnightTick();

  // Anchored to the response's own `checked_at` (not Date.now()) so
  // days-blocked and the checked-at line always agree, and so a fixed
  // response (e.g. a story/test fixture) renders deterministically.
  const asOf = useMemo(
    () => (data?.checked_at ? new Date(data.checked_at) : new Date()),
    [data?.checked_at],
  );

  const rows = useMemo(() => buildPayerConnectionsRoster(data, asOf), [data, asOf]);

  // `formatCheckedAtLine`'s displayed time always comes from `checked_at`
  // itself — `now` only decides the same-day/different-day branch — so
  // `midnightTick` (which only changes at a local-midnight boundary) is a
  // correct "now" for this comparison without needing to be a live clock.
  const checkedAtLine = formatCheckedAtLine(data?.checked_at, midnightTick);

  const columns = useMemo(
    () => [
      {
        key: 'organizationName' as const,
        label: 'Organization',
        sortable: false,
        render: (value: unknown) => (
          <Typography sx={{ fontWeight: 600 }}>{String(value)}</Typography>
        ),
      },
      {
        key: 'blockedParty' as const,
        label: 'Blocked on',
        sortable: false,
        render: (_value: unknown, row: RosterTableRow) =>
          row.isError ? (
            <Typography color="text.secondary">—</Typography>
          ) : (
            <BlockedOnChip party={row.blockedParty ?? 'NONE'} />
          ),
      },
      {
        key: 'daysBlocked' as const,
        label: 'Days',
        sortable: false,
        render: (_value: unknown, row: RosterTableRow) =>
          row.isError ? (
            <Typography color="text.secondary">—</Typography>
          ) : row.daysBlocked == null ? (
            <Tooltip title={NO_TIMESTAMP_TOOLTIP}>
              <span className={getStalenessColor(null)}>—</span>
            </Tooltip>
          ) : (
            <span className={getStalenessColor(row.daysBlocked)}>
              {formatDaysBlocked(row.daysBlocked)}
            </span>
          ),
      },
      {
        key: 'payers' as const,
        label: 'Payers',
        sortable: false,
        render: (_value: unknown, row: RosterTableRow) =>
          row.isError ? (
            <Typography color="error.main" data-testid="roster-row-error">
              {row.errorMessage}
            </Typography>
          ) : (
            <RosterChipStrip payers={row.payers} liveCount={row.liveCount} />
          ),
      },
      {
        key: 'organizationId' as const,
        label: '',
        sortable: false,
        render: (value: unknown) => (
          <Button
            component={Link}
            to={`/admin-v2/organization/${String(value)}?tab=intake-hub`}
            size="small"
            variant="outlined"
            onClick={event => event.stopPropagation()}
          >
            Open clinic
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-8">
        <BackButton />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, mb: 1 }}>
          <Hub color="primary" fontSize="large" />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Payer Connections
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Every organization blocked on a payer enrollment, worst first
            </Typography>
          </Box>
        </Box>

        {checkedAtLine && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {checkedAtLine}
          </Typography>
        )}

        {!isLoading && error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => void refetch()}>
                Retry
              </Button>
            }
          >
            The clearinghouse could not be reached. This is a request failure, not a roster
            result.
          </Alert>
        )}

        {!error && (
          <FilterableDataTable<RosterTableRow>
            title="Organizations"
            columns={columns}
            data={rows}
            total={rows.length}
            loading={isLoading}
            searchable={false}
            emptyMessage="No organizations are blocked on anyone."
          />
        )}
      </main>
    </div>
  );
}
